import { styled, css } from "styled-components";
import theme from "styled-theming";

import Icon from "components/Icon";
import Link from "components/Link";
import { Container as GridContainer } from "style/components/Page";
import {
    BodyL,
    styleBodyM,
    styleHeadingM,
    styleHeadingXL,
    styleLabelM,
} from "style/components/Typography";
import { columnGap } from "style/grid";
import { MQ } from "style/mediaQueries";

const background = theme("theme", {
    lightgray: "--background-default",
    blue: "--block-blue-default",
    cyan: "--block-cyan-default",
});

const circularColor = theme("theme", {
    lightgray: "--icon-on-neutral-disabled",
    blue: "--icon-on-blue-disabled",
    cyan: "--icon-on-cyan-disabled",
});

const indicatorBackgroundActive = theme("theme", {
    lightgray: "--icon-on-neutral-primary",
    blue: "--icon-on-blue-primary",
    cyan: "--icon-on-cyan-primary",
});

const indicatorColorActive = theme("theme", {
    lightgray: "--text-inverted",
    blue: "--text-on-cyan-primary",
    cyan: "--text-on-blue-primary",
});

const indicatorColor = theme("theme", {
    lightgray: "--text-on-neutral-disabled",
    blue: "--text-on-blue-disabled",
    cyan: "--text-on-cyan-disabled",
});

const linkColor = theme("theme", {
    lightgray: "--text-on-neutral-secondary",
    blue: "--text-on-blue-secondary",
    cyan: "--text-on-cyan-secondary",
});

const hoverColor = theme("contentTheme", {
    black: "--text-on-neutral-link",
    cyan: "--text-on-blue-primary-alt",
    blue: "--text-on-cyan-primary",
    white: "--text-on-neutral-inverted",
});

export const Content = styled(GridContainer)`
    ${columnGap}
    display: grid;
    grid-template: auto 1fr / repeat(4, 1fr);
    grid-template-areas:
        "heading"
        "introduction"
        "steps";
    width: 100%;

    ${MQ.FROM_M} {
        grid-template-columns: repeat(12, 1fr);
    }

    ${MQ.FROM_L} {
        grid-template-areas:
            "heading steps"
            "introduction steps";
    }
`;

export const Heading = styled.h2`
    ${styleHeadingXL}
    grid-area: heading;
    grid-column: 1 / span 3;
    justify-self: flex-start;
    margin-bottom: 1.3125rem;
    margin-top: 0;

    ${MQ.FROM_M} {
        grid-column: 1 / span 8;
    }

    ${MQ.FROM_L} {
        grid-column: 1 / span 6;
    }

    ${MQ.FROM_XL} {
        margin-bottom: 1.75rem;
    }
`;

export const Introduction = styled(BodyL)`
    grid-area: introduction;
    grid-column: 1 / span 4;
    justify-self: flex-start;

    ${MQ.FROM_M} {
        grid-column: 1 / span 8;
    }

    ${MQ.FROM_L} {
        grid-column: 1 / span 5;
    }
`;

export const StepsWrapper = styled.div`
    --circular-size: 1.75rem;
    --indicator-size: 2.625rem;

    grid-area: steps;
    grid-column: 1 / span 4;
    position: relative;

    ${MQ.FROM_M} {
        --circular-size: 2.625rem;

        grid-column: 5 / span 8;
    }

    ${MQ.FROM_L} {
        grid-column: 7 / span 6;
    }

    ${MQ.FROM_XL} {
        --circular-size: 3.5rem;
        --indicator-size: 3.5rem;

        grid-column: 7 / span 5;
    }
`;

export const Steps = styled.ol`
    counter-reset: item;
    display: flex;
    flex-direction: column;
    margin: 2.625rem 0 1.75rem;
    padding-left: 1.25rem;
    row-gap: 1.3125rem;

    ${MQ.FROM_M} {
        margin: 3.5rem 0 2.625rem;
    }

    ${MQ.FROM_L} {
        margin: 3.9375rem 0 2.625rem;
    }

    ${MQ.FROM_XL} {
        margin: 5.25rem 0 3.5rem;
        padding-left: 1.6875rem;
        row-gap: 1.75rem;
    }
`;

export const StepIcon = styled(Icon)`
    color: var(--icon-decorative-coral);
    margin-bottom: 0.4375rem;

    ${MQ.FROM_XL} {
        margin-bottom: 0.875rem;
    }
`;

export const StepHeading = styled.h3`
    ${styleHeadingM}
    margin-block: 0;
`;

export const StepContent = styled.p`
    ${styleBodyM}
    margin-block: 0;
`;

export const LinkText = styled.div`
    ${styleLabelM};
    color: var(${linkColor});
    margin-right: 0.875rem;
    transition:
        color 0.2s ease-in-out,
        margin-right 0.2s ease-in-out;
`;

export const IconArrow = styled(Icon)`
    color: var(${linkColor});
    margin-right: 0.875rem;
    transition:
        color 0.2s ease-in-out,
        margin-left 0.2s ease-in-out;
`;

export const ReadMore = styled(Link)`
    border-bottom: none;
    display: block;
    transition: color 0.2s ease-in-out;

    &:hover {
        ${LinkText} {
            color: var(${hoverColor});
            margin-right: 0;
        }

        ${IconArrow} {
            color: var(${hoverColor});
            margin-left: 0.875rem;
        }
    }
`;

export const CircularProcess = styled.div`
    border: 1px dashed var(${circularColor});
    border-radius: calc(var(--circular-size) / 2);
    border-right: none;
    bottom: 0;
    left: 0;
    margin-left: -0.4375rem;
    position: absolute;
    top: 0.875rem;
    width: var(--circular-size);

    ${MQ.FROM_M} {
        margin-left: calc(var(--circular-size) / -2);
    }

    ${MQ.FROM_L} {
        top: 1.3125rem;
    }

    ${MQ.FROM_XL} {
        top: 1.75rem;
    }
`;

export const Step = styled.li.attrs<{
    $hasIcon: boolean;
    $enableNumbers: boolean;
    $active: boolean;
}>(() => ({}))`
    border-left: 1px solid var(${circularColor});
    display: flex;
    flex-direction: column;
    padding-bottom: 2.625rem;
    padding-left: 2.625rem;
    position: relative;
    row-gap: 0.875rem;
    transition: border-color 0.2s ease-in-out;

    ${(props) =>
        !props.$hasIcon &&
        css`
            ${StepHeading} {
                margin-top: 1.3125rem;

                ${MQ.FROM_XL} {
                    margin-top: 1.75rem;
                }
            }
        `}
    ${(props) =>
        props.$active &&
        css`
            border-color: var(${indicatorBackgroundActive});
        `}

    ${MQ.FROM_M} {
        padding-left: 3.5rem;
        padding-bottom: 3.5rem;
    }

    ${MQ.FROM_XL} {
        padding-left: 4.375rem;
        padding-bottom: 4.375rem;
    }

    &::before {
        ${styleHeadingM};
        align-items: center;
        aspect-ratio: 1;
        background-color: var(${background});
        border: 1px solid var(${circularColor});
        border-radius: calc(var(--indicator-size) / 2);
        color: var(${indicatorColor});
        counter-increment: item;
        display: flex;
        justify-content: center;
        left: calc(var(--indicator-size) / -2);
        margin: 0;
        position: absolute;
        transition:
            border-color 0.2s ease-in-out,
            color 0.2s ease-in-out,
            background-color 0.2s ease-in-out;
        top: 4.375rem;
        width: var(--indicator-size);
        ${(props) =>
            props.$enableNumbers
                ? css`
                      content: counter(item);
                  `
                : css`
                      content: "";
                  `};
        ${(props) =>
            !props.$hasIcon &&
            css`
                top: 0.6875rem;

                ${MQ.FROM_XL} {
                    top: 1.125rem;
                }
            `}
        ${(props) =>
            props.$active &&
            css`
                background-color: var(${indicatorBackgroundActive});
                border-color: var(${indicatorBackgroundActive});
                color: var(${indicatorColorActive});
            `}
    }
`;
